var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography, FormHelperText, CircularProgress } from '@mui/material';
import { isEmpty } from 'lodash';
import { useDropzone } from 'react-dropzone';
import FileInfo from './FileInfo';
import useStyles, { isDragRejected, isFocusedOrDragAccepted } from './styles';
const ExtensivFileUpload = forwardRef((_a, ref) => {
    var { dataTestId = '', hasLabel = false, label = '', required = false, showFileSpecs = false, fileSpecs = '', error = false, errorMessage, instructions = 'Drop files here or', isLoading, multiple = true, onFilesAccepted = () => { }, onFilesRejected = () => { }, onFileRemove = () => { }, dropzoneOptions = {}, files, defaultImagePreviewUrl = null, wrapperClassName, className: propClassName } = _a, props = __rest(_a, ["dataTestId", "hasLabel", "label", "required", "showFileSpecs", "fileSpecs", "error", "errorMessage", "instructions", "isLoading", "multiple", "onFilesAccepted", "onFilesRejected", "onFileRemove", "dropzoneOptions", "files", "defaultImagePreviewUrl", "wrapperClassName", "className"]);
    const cmpSelector = dataTestId || 'ExtensivFileUpload';
    const { classes, cx } = useStyles();
    const [acceptedFilesArray, setAcceptedFilesArray] = useState([]);
    const [rejectionFilesArray, setRejectionFilesArray] = useState([]);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [acceptableFileTypes, setAcceptableFileTypes] = useState([]);
    const [style, setStyle] = useState({});
    const extraDropzoneOptions = {
        disabled: (dropzoneOptions === null || dropzoneOptions === void 0 ? void 0 : dropzoneOptions.disabled) || isLoading || false,
        multiple,
    };
    const { isDragActive, getRootProps, getInputProps, isDragReject, isDragAccept, isFocused, acceptedFiles, fileRejections, open } = useDropzone(Object.assign(Object.assign({}, dropzoneOptions), extraDropzoneOptions));
    useImperativeHandle(ref, () => ({ open }), [open]);
    useEffect(() => {
        setStyle(Object.assign(Object.assign(Object.assign({}, (isFocused && (errorMessage === '' || error === false) ? isFocusedOrDragAccepted : {})), (isDragAccept ? isFocusedOrDragAccepted : {})), (isDragReject ? isDragRejected : {})));
    }, [isFocused, isDragAccept, isDragReject]);
    useEffect(() => {
        if (!Array.isArray(files)) {
            if (files instanceof File) {
                setAcceptedFilesArray([files]);
                setPreviewFile(files);
            }
        }
        else if (files.length > 0) {
            setAcceptedFilesArray(files);
        }
        else {
            setImagePreviewUrl(null);
            setPreviewFile(null);
            setAcceptedFilesArray([]);
        }
    }, [files]);
    useEffect(() => {
        if (defaultImagePreviewUrl !== '' && typeof defaultImagePreviewUrl === 'string') {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                setImagePreviewUrl(defaultImagePreviewUrl);
                try {
                    const response = yield fetch(defaultImagePreviewUrl);
                    const blob = yield response.blob();
                    const results = new RegExp(/^image\/.+[a-zA-Z]$/, 'i').exec(blob.type);
                    if (results !== null) {
                        const fileName = new URL(defaultImagePreviewUrl).pathname.split('/').pop();
                        const tempFile = new File([blob], fileName || '', { type: blob.type });
                        setAcceptedFilesArray([tempFile]);
                        setPreviewFile(tempFile);
                    }
                }
                catch (Exception) {
                    const tempFile = new File([], 'Couldn\'t get file information', { type: 'image/jpeg' });
                    setAcceptedFilesArray([tempFile]);
                    setPreviewFile(tempFile);
                }
            }))();
        }
    }, [defaultImagePreviewUrl]);
    useEffect(() => {
        let tempFilesArray = [...acceptedFiles];
        if (acceptedFilesArray.length > 0 && multiple) {
            const tempFilesName = acceptedFilesArray.map(file => file.name);
            tempFilesArray = [...acceptedFilesArray, ...acceptedFiles.filter(file => !tempFilesName.includes(file.name))];
        }
        setAcceptedFilesArray(tempFilesArray);
        if (!multiple &&
            tempFilesArray.length === 1) {
            const tempPreviewFile = tempFilesArray[0];
            setPreviewFile(tempPreviewFile);
            if (tempPreviewFile.type.match(/image/))
                setImagePreviewUrl(URL.createObjectURL(tempPreviewFile));
        }
        return () => {
            if (imagePreviewUrl)
                URL.revokeObjectURL(imagePreviewUrl);
        };
    }, [acceptedFiles]);
    useEffect(() => {
        onFilesAccepted(acceptedFilesArray);
    }, [acceptedFilesArray]);
    useEffect(() => {
        setRejectionFilesArray(fileRejections);
        onFilesRejected(fileRejections);
    }, [fileRejections]);
    useEffect(() => {
        if (!isEmpty(dropzoneOptions.accept)) {
            const acceptedTypes = Object.entries(dropzoneOptions.accept).map(([key, value]) => {
                return value.length > 0 ? value.join(', ') : key;
            });
            setAcceptableFileTypes(acceptedTypes);
        }
    }, [dropzoneOptions.accept]);
    const removeFile = (fileIndex) => {
        const removedFile = acceptedFilesArray.splice(fileIndex, 1);
        setAcceptedFilesArray([...acceptedFilesArray]);
        if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl);
            setImagePreviewUrl(null);
        }
        onFileRemove(removedFile[0]);
    };
    const removeRejectedFileAndError = (index) => {
        rejectionFilesArray.splice(index, 1);
        setRejectionFilesArray([...rejectionFilesArray]);
    };
    return (_jsxs(Box, Object.assign({ className: wrapperClassName || '' }, { children: [hasLabel && label && (_jsxs(Box, Object.assign({ className: cx(classes.labelWrapper, { imagePreview: !multiple && acceptedFilesArray.length === 1 }) }, { children: [label, " ", required && `*`] }))), !multiple && imagePreviewUrl && (_jsx(Box, Object.assign({ className: classes.imagePreviewWrapper, "data-testid": cmpSelector }, { children: _jsx(Box, Object.assign({ className: 'imageWrapper' }, { children: _jsx("img", { className: 'image', alt: previewFile === null || previewFile === void 0 ? void 0 : previewFile.name, "data-testid": `${cmpSelector}ImagePreview`, src: imagePreviewUrl }) })) }))), ((multiple || acceptedFilesArray.length === 0 || acceptedFilesArray.length > 1) || (!imagePreviewUrl)) && (_jsxs(Box, Object.assign({ className: cx(classes.dropzone, propClassName, {
                    'error': error,
                    'disabled': extraDropzoneOptions.disabled
                }), "data-testid": cmpSelector }, getRootProps({ style }), props, { children: [_jsx("input", Object.assign({}, getInputProps(), { "data-testid": `${cmpSelector}Input` })), _jsxs(Box, Object.assign({ className: 'content-section', "data-testid": `${cmpSelector}Content` }, { children: [_jsx(FontAwesomeIcon, { icon: faCloudArrowUp, scale: '3x', className: 'content-icon' }), _jsxs(Box, Object.assign({ className: 'text-section' }, { children: [!isDragActive && (_jsxs(_Fragment, { children: [_jsx(Typography, Object.assign({ className: 'typography-text' }, { children: (!dropzoneOptions.noDrag) && instructions })), _jsx(Typography, Object.assign({ className: 'typography-text' }, { children: (!dropzoneOptions.noClick) && (_jsx("span", Object.assign({ className: 'uploadLink', "data-testid": `${cmpSelector}UploadFromComputerBtn` }, { children: "browse files" }))) }))] })), (isDragActive && !isDragReject) && (_jsx(Typography, Object.assign({ className: 'typography-text' }, { children: 'Drop it here' })))] }))] })), _jsxs(Box, Object.assign({ className: classes.fileValidationSection }, { children: [(acceptableFileTypes.length > 0) && (_jsx(Typography, Object.assign({ className: classes.acceptedFilesTypography, "data-testid": `${cmpSelector}FileTypes` }, { children: acceptableFileTypes.join(', ') }))), showFileSpecs ? _jsx(Typography, Object.assign({ className: classes.fileSpecsText, "data-testid": `${cmpSelector}FileSpecs` }, { children: fileSpecs })) : ''] }))] }))), isLoading && (_jsxs(Box, Object.assign({ className: classes.loadingSection }, { children: [_jsx(CircularProgress, { className: 'content-loader', "data-testid": `${cmpSelector}BoxLoader` }), " Loading..."] }))), (errorMessage || isDragReject) && (_jsx(Box, Object.assign({ className: classes.errorMessageBox }, { children: _jsx(FormHelperText, Object.assign({ className: 'error-message', error: true, "data-testid": `${cmpSelector}ErrorMessage` }, { children: errorMessage ? errorMessage : 'Oops! Something went wrong.' })) }))), acceptedFilesArray.length > 0 && (acceptedFilesArray.map((file, index) => (_jsx(FileInfo, { file: file, fileIndex: index, onFileDelete: removeFile, disabled: extraDropzoneOptions.disabled }, index)))), rejectionFilesArray.length > 0 && (rejectionFilesArray.map((fileObject, index) => (_jsx(FileInfo, { isInvalid: true, file: fileObject.file, fileIndex: index, errors: fileObject.errors, onFileDelete: removeRejectedFileAndError }, index))))] })));
});
export default ExtensivFileUpload;
//# sourceMappingURL=index.js.map